/**
 * StatusBadge component - Displays status badges for documents and other entities
 * 
 * Features:
 * - Multiple status types: pending, approved, rejected, verified, active, completed, in_progress
 * - Color-coded badges matching UX Design specification
 * - Accessible ARIA labels
 * - Optional label text
 * 
 * Colors:
 * - Pending: Yellow/Orange (#FF9900)
 * - Approved/Verified: Green (#00AA44)
 * - Rejected: Red (#CC0000)
 * - Active: Blue (#0066CC)
 * - Completed/In Progress: Gray
 */
interface StatusBadgeProps {
  status: 'pending' | 'approved' | 'rejected' | 'verified' | 'active' | 'completed' | 'in_progress';
  label?: string;
}

export const StatusBadge = ({ status, label }: StatusBadgeProps) => {
  // Status configuration
  const statusConfig = {
    pending: {
      bgColor: 'bg-warning/10',
      textColor: 'text-warning',
      defaultLabel: 'Pending',
    },
    approved: {
      bgColor: 'bg-success/10',
      textColor: 'text-success',
      defaultLabel: 'Approved',
    },
    rejected: {
      bgColor: 'bg-error/10',
      textColor: 'text-error',
      defaultLabel: 'Rejected',
    },
    verified: {
      bgColor: 'bg-success/10',
      textColor: 'text-success',
      defaultLabel: 'Verified',
    },
    active: {
      bgColor: 'bg-primary/10',
      textColor: 'text-primary',
      defaultLabel: 'Active',
    },
    completed: {
      bgColor: 'bg-text-tertiary/10',
      textColor: 'text-text-tertiary',
      defaultLabel: 'Completed',
    },
    in_progress: {
      bgColor: 'bg-text-tertiary/10',
      textColor: 'text-text-tertiary',
      defaultLabel: 'In Progress',
    },
  };

  const config = statusConfig[status];
  const displayLabel = label || config.defaultLabel;

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-1 ${config.bgColor} ${config.textColor} rounded-md text-small font-medium`}
      aria-label={`Status: ${displayLabel}`}
      role="status"
    >
      <span>{displayLabel}</span>
    </div>
  );
};

